// ./src/utils/tireWarningHelpers.ts
//
// Shared constants and helpers for tire warning flags display.
// These complement the TIRE_WARNING_FLAGS and helper functions
// already exported from TireSetService.ts.

import { TIRE_WARNING_FLAGS } from "./api/TireSetService";

// =============================================================================
// Warning Flag i18n Key Mapping
// =============================================================================

/**
 * Maps each warning flag bit to its i18n translation key.
 * Used by UI components to display human-readable warning messages.
 */
export const TIRE_WARNING_I18N_KEYS: Record<number, string> = {
  [TIRE_WARNING_FLAGS.AGE_WARNING]: "tireSets:warning.ageWarning",
  [TIRE_WARNING_FLAGS.AGE_CRITICAL]: "tireSets:warning.ageCritical",
  [TIRE_WARNING_FLAGS.MILEAGE_WARNING]: "tireSets:warning.mileageWarning",
  [TIRE_WARNING_FLAGS.MILEAGE_CRITICAL]: "tireSets:warning.mileageCritical",
  [TIRE_WARNING_FLAGS.TREAD_WARNING]: "tireSets:warning.treadWarning",
  [TIRE_WARNING_FLAGS.TREAD_CRITICAL]: "tireSets:warning.treadCritical",
  [TIRE_WARNING_FLAGS.SEASONAL_MISMATCH]: "tireSets:warning.seasonalMismatch",
  [TIRE_WARNING_FLAGS.STORAGE_LONG]: "tireSets:warning.storageLong",
  [TIRE_WARNING_FLAGS.TREAD_STALE]: "tireSets:warning.treadStale",
};

/**
 * All warning flag bits in display order.
 * Critical flags are listed right after their warning counterparts.
 */
export const ALL_WARNING_BITS: number[] = [
  TIRE_WARNING_FLAGS.AGE_WARNING,
  TIRE_WARNING_FLAGS.AGE_CRITICAL,
  TIRE_WARNING_FLAGS.MILEAGE_WARNING,
  TIRE_WARNING_FLAGS.MILEAGE_CRITICAL,
  TIRE_WARNING_FLAGS.TREAD_WARNING,
  TIRE_WARNING_FLAGS.TREAD_CRITICAL,
  TIRE_WARNING_FLAGS.SEASONAL_MISMATCH,
  TIRE_WARNING_FLAGS.STORAGE_LONG,
  TIRE_WARNING_FLAGS.TREAD_STALE,
];

/**
 * Item-level warning bits only (excludes set-level-only flags:
 * SEASONAL_MISMATCH and STORAGE_LONG).
 */
export const ITEM_WARNING_BITS: number[] = [
  TIRE_WARNING_FLAGS.AGE_WARNING,
  TIRE_WARNING_FLAGS.AGE_CRITICAL,
  TIRE_WARNING_FLAGS.MILEAGE_WARNING,
  TIRE_WARNING_FLAGS.MILEAGE_CRITICAL,
  TIRE_WARNING_FLAGS.TREAD_WARNING,
  TIRE_WARNING_FLAGS.TREAD_CRITICAL,
  TIRE_WARNING_FLAGS.TREAD_STALE,
];

/**
 * Critical flag bits for quick severity check.
 */
export const CRITICAL_FLAG_BITS: Set<number> = new Set([
  TIRE_WARNING_FLAGS.AGE_CRITICAL,
  TIRE_WARNING_FLAGS.MILEAGE_CRITICAL,
  TIRE_WARNING_FLAGS.TREAD_CRITICAL,
]);

// =============================================================================
// Helper Functions
// =============================================================================

export interface WarningMessage {
  /** The flag bit value */
  bit: number;
  /** The i18n key for the message */
  i18nKey: string;
  /** Whether this is a critical-level warning */
  isCritical: boolean;
}

/**
 * Decode a warning flags bitmask into an array of WarningMessage objects.
 *
 * @param flags - The bitmask to decode
 * @param bits - Which bits to check (default: ALL_WARNING_BITS)
 * @returns Array of active warning messages with severity info
 */
export function getWarningMessages(
  flags: number | null | undefined,
  bits: number[] = ALL_WARNING_BITS
): WarningMessage[] {
  if (!flags) return [];
  return bits
    .filter((bit) => (flags & bit) > 0)
    .map((bit) => ({
      bit,
      i18nKey: TIRE_WARNING_I18N_KEYS[bit],
      isCritical: CRITICAL_FLAG_BITS.has(bit),
    }))
    .filter((msg) => msg.i18nKey); // safety: skip unmapped bits
}

/**
 * Get translated warning message strings from a flags bitmask.
 * Convenience wrapper around getWarningMessages for simple tooltip use.
 *
 * @param flags - The bitmask to decode
 * @param t - i18n translation function
 * @param bits - Which bits to check (default: ALL_WARNING_BITS)
 * @returns Array of translated warning strings
 */
export function getTranslatedWarnings(
  flags: number | null | undefined,
  t: (key: string) => string,
  bits: number[] = ALL_WARNING_BITS
): string[] {
  return getWarningMessages(flags, bits).map((msg) => t(msg.i18nKey));
}